
import { Component, Vue } from "vue-property-decorator";

import { IRouteMeta } from "@/router/interface";
import structure_routes from "@/api/routes/structures";
import skill_routes from "@/api/routes/skills";
import block_routes from "@/api/routes/blocks";

import Preloader from "@/components/Preloader.vue";
import Search from "@/components/Search.vue";
import CPaginatedSelect from "@/components/PaginatedSelect.vue";

import SkillCategory from "@/models/skill/category";
import SkillLocal from "@/models/skill/local";
import BlockBlock from "@/models/block/block";
import StructureFactory from "@/models/structure/factory";

import CProgramListStructure from "./components/ListStructure.vue";
import CProgramListMethodical from "./components/ListMethodical.vue";
import CProgramListSearch from "./components/ListSearch.vue";
import CProgramInfinityList from "./components/InfinityList.vue";

@Component({
  name: "VConstructorAll",
  components: {
    Search,
    Preloader,
    CPaginatedSelect,
    CProgramListStructure,
    CProgramListMethodical,
    CProgramListSearch,
    CProgramInfinityList
  }
})
export default class VConstructorAll extends Vue {
  public preload: boolean = false;

  public filters: Map<string, boolean> = new Map();
  public show_filter: boolean = false;
  public filters_count: number = 0;

  private show_list_search: boolean = false;
  private search: string = this.$store.state.search;

  public factories: StructureFactory[] = [];
  public current_factory: StructureFactory | null = null;

  public block_id: string | null = null;
  public blocks_options: BlockBlock[] = [{ id: null, full_name: "Все" }];

  public category_id: number | null = null;
  public categories_options: SkillCategory[] = [{ id: null, value: "Все" }];

  private reload_skills_fn: Function | null = null;
  private skill: SkillLocal | null = null;
  private skill_id: string | null = null;

  $refs!: {
    tabs: HTMLDivElement;
  };

  private watchers: Function[] = [];

  protected beforeDestroy() {
    this.watchers.forEach(unwatch => {
      unwatch();
    });
  }

  protected async created() {
    this.preload = true;

    this.watchers.push(
      this.$store.watch(
        state => state.search,
        search => {
          this.search = search;

          this.changeShowListSearch();
        }
      )
    );

    const promises = [this.loadCategories(), this.loadBlocks()];

    if (this.$api.allowOnlyUserWorkplaces()) {
      promises.push(this.loadFactory());
    } else {
      promises.push(this.loadFactories());
    }

    await Promise.all(promises);

    this.setCurrentFactory();

    this.preload = false;
  }

  private changeShowListSearch() {
    this.show_list_search =
      this.search.length > 0 || this.category_id != null || this.skill != null;
  }

  public clearFilters() {
    this.filters.clear();

    this.category_id = null;
    this.block_id = null;
    this.skill = null;
    this.skill_id = null;

    this.filters_count = this.filters.size;

    if (this.reload_skills_fn) {
      this.reload_skills_fn.call(null);
    }

    this.changeShowListSearch();
  }

  private async loadCategories() {
    return this.$api
      .get(skill_routes.categories, {
        params: {
          section_id: (this.$router.currentRoute.meta! as IRouteMeta)
            .permissions?.[0]
        }
      })
      .then(({ data: res }: { data: SkillCategory[] }) => {
        this.categories_options.push(...res);
      });
  }

  private async loadBlocks() {
    return this.$api
      .get(block_routes.blocks, {
        params: {
          section_id: (this.$router.currentRoute.meta! as IRouteMeta)
            .permissions?.[0]
        }
      })
      .then(({ data: res }: { data: { blocks: BlockBlock[] } }) => {
        this.blocks_options.push(...res.blocks);
      });
  }

  public async changeCategory() {
    if (this.category_id) {
      this.filters.set("category_id", true);
    } else {
      this.filters.delete("category_id");
    }

    this.filters_count = this.filters.size;

    if (this.reload_skills_fn) {
      this.reload_skills_fn.call(null);
    }

    this.changeShowListSearch();
  }

  public async changeBlock() {
    if (this.block_id) {
      this.filters.set("block_id", true);
    } else {
      this.filters.delete("block_id");
    }

    this.filters_count = this.filters.size;

    if (this.reload_skills_fn) {
      this.reload_skills_fn.call(null);
    }
  }

  public async changeSkill(skill: SkillLocal | null) {
    this.skill = skill;

    if (this.skill) {
      this.skill_id = this.skill.id;
      this.filters.set("skill", true);
    } else {
      this.skill_id = null;

      this.filters.delete("skill");
    }

    this.filters_count = this.filters.size;

    this.changeShowListSearch();
  }

  public setReloadSkills(reload_fn: Function) {
    this.reload_skills_fn = reload_fn;
  }

  public async loadSkillFn(page: number = 0, search_query: string) {
    return this.$api
      .get(skill_routes.skills, {
        params: {
          executed_skill_in_program: false,
          only_is_local: false,
          responsible: false,

          block_id: this.block_id,
          category_id: this.category_id,

          exclude_archived: true,

          factory_id: this.current_factory?.id,
          ignore_structure: false,

          section_id: (this.$router.currentRoute.meta! as IRouteMeta)
            .permissions?.[0],

          page,
          per_page: 50,
          paginate: true,
          search: search_query
        }
      })
      .then(({ data: res }: { data: { skills: SkillLocal[] } }) => {
        if (res.skills.length) {
          return res.skills;
        }

        return [];
      });
  }

  private setCurrentFactory() {
    const factory_program_id = this.$store.state.factoryProgramId;

    if (factory_program_id && this.factories.length) {
      const target_factory = this.factories.find(
        i => i.id === factory_program_id
      );

      if (target_factory) {
        this.current_factory = target_factory;
      }
    }

    if (!this.current_factory) {
      this.current_factory = this.factories[0];
    }
  }

  public showFactory(factory: StructureFactory) {
    this.current_factory = factory;
  }

  private async loadFactory() {
    return this.$api
      .get(structure_routes.me_factory)
      .then(({ data: res }: { data: StructureFactory }) => {
        this.factories = [res];
      });
  }

  private async loadFactories() {
    return this.$api
      .get(structure_routes.factories, {
        params: {
          only_my: true,
          section_id: (this.$router.currentRoute.meta! as IRouteMeta)
            .permissions?.[0]
        }
      })
      .then(({ data: res }: { data: StructureFactory[] }) => {
        this.factories = res;
      });
  }

  public tabScrollRight() {
    this.$refs.tabs.scrollLeft += 100;
  }

  public tabScrollLeft() {
    this.$refs.tabs.scrollLeft -= 100;
  }

  public allowWrite() {
    return this.$api.allowWrite();
  }
}
